import { Link, useParams } from 'react-router-dom';
import type { EgressPathSwitchReason } from '@nadoutong/contracts';
import { useEgressSnapshots, useEgressSwitchEvents } from '../api/useEgressLines.ts';
import { useT } from '../i18n/index.ts';
import {
  Chip,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';
import type { ChipTone } from '../ui/index.ts';
import { NodeIdentity } from './egress-lines/NodeIdentity.tsx';
import { UpstreamLabel } from './egress-lines/UpstreamLabel.tsx';

/**
 * `/supply/egress-lines/:nodeId` — one node's path scores and switch timeline.
 */

export function EgressLineNodePage(): JSX.Element {
  const t = useT();
  const params = useParams<{ nodeId: string }>();
  const nodeId = params.nodeId ?? '';
  const snapshotsQ = useEgressSnapshots();
  const eventsQ = useEgressSwitchEvents();
  const paths = (snapshotsQ.data ?? []).filter((row) => row.node_id === nodeId);
  const events = (eventsQ.data ?? []).filter((row) => row.node_id === nodeId);
  const selected = paths.filter((row) => row.selected);
  const gatewayRegion = paths[0]?.gateway_region ?? '—';

  return (
    <section className="flex flex-col gap-6" data-testid="egress-line-node-page">
      <div>
        <nav aria-label="breadcrumb" className="mb-2 flex items-center gap-2 text-dense text-paper-low">
          <Link to="/supply/egress-lines" className="transition-colors duration-120 hover:text-paper">
            {t('egress_lines.title')}
          </Link>
          <span aria-hidden>/</span>
          <NodeIdentity nodeId={nodeId} region={gatewayRegion} />
        </nav>
        <PageHeader
          title={t('egress_lines.node.title', { node: nodeId })}
          sub={t('egress_lines.node.subtitle', { region: gatewayRegion })}
        />
      </div>

      <Panel testId="egress-line-node-paths" caption={t('egress_lines.node.paths')} padded>
        {paths.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.node.paths_empty')}</p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th>{t('egress_lines.node.path')}</Th>
                <Th>{t('egress_lines.node.sort_key')}</Th>
                <Th>{t('egress_lines.node.success')}</Th>
                <Th>{t('egress_lines.node.samples')}</Th>
                <Th>{t('egress_lines.node.qualified')}</Th>
                <Th>{t('egress_lines.node.selected')}</Th>
                <Th>{t('egress_lines.node.dwell')}</Th>
              </Tr>
            </THead>
            <TBody>
              {paths.map((path) => (
                <Tr key={path.path_id} data-testid={`egress-line-path-${path.path_id}`}>
                  <Td className="font-mono text-dense">{path.path_id}</Td>
                  <Td num>{path.p75_sort_key_ms === null ? '—' : `${Math.round(path.p75_sort_key_ms)}ms`}</Td>
                  <Td num>{path.success_rate === null ? '—' : `${Math.round(path.success_rate * 100)}%`}</Td>
                  <Td num>{path.sample_count}</Td>
                  <Td>
                    <Chip tone={path.qualified ? 'ok' : 'warn'} lamp={false}>
                      {path.qualified ? t('egress_lines.node.yes') : t('egress_lines.node.no')}
                    </Chip>
                  </Td>
                  <Td>
                    {path.selected ? (
                      <Chip tone="ok" lamp={false}>{t('egress_lines.node.live_chip')}</Chip>
                    ) : '—'}
                  </Td>
                  <Td className="font-mono text-micro text-paper-low">{path.dwell_started_at ?? '—'}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
        {selected.length > 0 ? (
          <p className="m-0 mt-3 text-dense text-paper-mid" data-testid="egress-line-node-selection">
            {t('egress_lines.node.selection', {
              path: selected.map((row) => row.path_id).join(', '),
              dwell: selected[0]?.dwell_started_at ?? '—',
            })}
          </p>
        ) : null}
      </Panel>

      <Panel testId="egress-line-node-traffic" caption={t('egress_lines.node.traffic')} padded>
        {paths.every((path) => path.target_observations.length === 0) ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.traffic.empty')}</p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th>{t('egress_lines.node.path')}</Th>
                <Th>{t('egress_lines.node.upstream')}</Th>
                <Th>{t('egress_lines.node.ttfb')}</Th>
                <Th>{t('egress_lines.node.sort_key')}</Th>
                <Th>{t('egress_lines.node.samples')}</Th>
                <Th>{t('egress_lines.node.last_explore')}</Th>
                <Th>{t('egress_lines.node.live')}</Th>
              </Tr>
            </THead>
            <TBody>
              {paths.flatMap((path) => path.target_observations.map((target) => (
                <Tr
                  key={`${path.path_id}:${target.upstream_id}`}
                  data-testid={`egress-line-traffic-${path.path_id}-${target.upstream_id}`}
                >
                  <Td className="font-mono text-dense">{path.path_id}</Td>
                  <Td>
                    <UpstreamLabel
                      upstreamId={target.upstream_id}
                      name={target.upstream_name}
                      region={target.upstream_region}
                    />
                  </Td>
                  <Td num>{target.p50_ttfb_ms === null ? '—' : `${Math.round(target.p50_ttfb_ms)}ms`}</Td>
                  <Td num>{target.p75_sort_key_ms === null ? '—' : `${Math.round(target.p75_sort_key_ms)}ms`}</Td>
                  <Td num>{target.sample_count}</Td>
                  <Td className="font-mono text-micro text-paper-low">{target.last_explored_at ?? '—'}</Td>
                  <Td>
                    {target.selected ? (
                      <Chip tone="ok" lamp={false}>{t('egress_lines.node.live_chip')}</Chip>
                    ) : '—'}
                  </Td>
                </Tr>
              )))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Panel testId="egress-line-node-events" caption={t('egress_lines.node.timeline')} padded>
        {events.length === 0 ? (
          <p className="m-0 text-dense text-paper-low">{t('egress_lines.node.timeline_empty')}</p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th>{t('egress_lines.node.when')}</Th>
                <Th>{t('egress_lines.node.dest')}</Th>
                <Th>{t('egress_lines.node.from')}</Th>
                <Th>{t('egress_lines.node.to')}</Th>
                <Th>{t('egress_lines.node.reason')}</Th>
                <Th>{t('egress_lines.node.scores')}</Th>
              </Tr>
            </THead>
            <TBody>
              {events.map((event) => (
                <Tr key={event.id} data-testid={`egress-line-event-${event.id}`}>
                  <Td className="font-mono text-micro">{event.switched_at}</Td>
                  <Td className="font-mono text-dense">{event.dest_region}</Td>
                  <Td className="font-mono text-dense">{event.from_path}</Td>
                  <Td className="font-mono text-dense">{event.to_path}</Td>
                  <Td>
                    <Chip tone={reasonTone(event.reason)} lamp={false}>{event.reason}</Chip>
                  </Td>
                  <Td className="font-mono text-micro text-paper-low">
                    {scoreMs(event.from_score)} → {scoreMs(event.to_score)}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>
    </section>
  );
}

function reasonTone(reason: EgressPathSwitchReason): ChipTone {
  if (reason === 'breaker') return 'bad';
  if (reason === 'recovery') return 'ok';
  if (reason === 'score') return 'signal';
  return 'neutral';
}

function scoreMs(score: Record<string, unknown> | null): string {
  if (score === null) return '—';
  const value = score.p75_sort_key_ms;
  return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}ms` : '—';
}
